import { ImageT } from '@lambda/apis/src/catalog-v2/types';
import classNames from 'classnames';
import Image from 'next/image';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import { buildProductImageAlt } from '../helpers';

type DesktopNavigationProps = {
  images: ImageT[];
  selectedImage: number;
  setSelectedImage: React.Dispatch<React.SetStateAction<number>>;
};

const SCROLL_HEIGHT = 48 * 4 + 8 * 3; // Height of 4 thumbnails + gaps

const DesktopNavigation = ({
  images,
  selectedImage,
  setSelectedImage,
}: DesktopNavigationProps) => {
  const {
    selectedSkuDetails: { condition, title, brandName },
  } = useProductPageContext();

  const imageAltAt = (index: number) =>
    buildProductImageAlt({ title, brandName, condition, position: index + 1 });

  const containerRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [showUpArrow, setShowUpArrow] = useState(false);
  const [showBottomArrow, setShowBottomArrow] = useState(false);

  const updateArrows = (target: HTMLElement) => {
    const { scrollTop, clientHeight, scrollHeight } = target;

    setShowUpArrow(scrollTop > 0);
    setShowBottomArrow(scrollTop + clientHeight < scrollHeight - 1);
  };

  // --- Scroll selected image into view when it changes
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const selectedThumb = container.children[
      selectedImage
    ] as HTMLElement | null;

    if (!selectedThumb) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const thumbRect = selectedThumb.getBoundingClientRect();

    // If the thumbnail is above the visible area
    if (thumbRect.top < containerRect.top) {
      container.scrollBy({
        top: thumbRect.top - containerRect.top,
        behavior: 'smooth',
      });
    }
    // If the thumbnail is below the visible area
    else if (thumbRect.bottom > containerRect.bottom) {
      container.scrollBy({
        top: thumbRect.bottom - containerRect.bottom,
        behavior: 'smooth',
      });
    }

    updateArrows(container);
  }, [selectedImage]);

  const shouldDisplayNavigation = images.length > 5;

  return (
    <div className="hidden space-y-2 lg:block">
      {shouldDisplayNavigation && (
        <button
          className="flex h-12 w-12 items-center justify-center"
          disabled={!showUpArrow}
          onClick={() => {
            const container = containerRef.current;

            if (!container) {
              return;
            }
            container.scrollTo({
              top: container.scrollTop - SCROLL_HEIGHT,
              behavior: 'smooth',
            });
            updateArrows(container);
          }}
        >
          {showUpArrow && <ChevronUp />}
        </button>
      )}

      <div
        ref={containerRef}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={classNames(
          'hide-scrollbar flex flex-col gap-2 overflow-y-auto',
          {
            'max-h-[calc((48px*4)+(8px*3))]': shouldDisplayNavigation, // 4 thumbnails + gaps
          },
        )}
      >
        {images.map((image, i) => (
          <button
            key={`thumbnail_${image.url}_${i}`}
            className={classNames(
              'block h-12 w-12 shrink-0 overflow-hidden rounded-md border border-gray-700 p-1 transition duration-150 ease-in-out focus:outline-none',
              {
                'border-gray-700/30 opacity-30 hover:opacity-100':
                  selectedImage !== i,
              },
            )}
            onClick={() => setSelectedImage(i)}
          >
            <Image
              className="h-full w-full object-contain"
              src={image.url}
              alt={imageAltAt(i)}
              height={100}
              width={100}
            />
          </button>
        ))}
      </div>

      {shouldDisplayNavigation && (
        <button
          className="flex h-12 w-12 items-center justify-center"
          disabled={!showBottomArrow}
          onClick={() => {
            const container = containerRef.current;

            if (!container) {
              return;
            }
            container.scrollTo({
              top: container.scrollTop + SCROLL_HEIGHT,
              behavior: 'smooth',
            });
            updateArrows(container);
          }}
        >
          {showBottomArrow && <ChevronDown />}
        </button>
      )}
    </div>
  );
};

export default DesktopNavigation;
